import React, { useMemo } from 'react';
import { Card, Typography, Row, Col, List } from 'antd';
import dayjs from 'dayjs';

import { TournamentType } from '../../../../enums/enums';
import { COLORS, DateFormatStrings } from '../../../../utils/constants';
import {
  EventFormState,
  FlattenedTournament,
  SportLiteType,
} from '../../../../types/types';
import styled from 'styled-components';
import { LoadingContainer } from './Step2Sports';
import Spinner from '../../../common/Spinner';
import { TournamentTypeMap } from '../../../../utils/eventUtils';

const { Title, Text } = Typography;

interface Props {
  formState: EventFormState;
  sportsData: SportLiteType[];
  isSportsLoading: boolean;
}

const Step4Review: React.FC<Props> = ({
  formState,
  sportsData,
  isSportsLoading,
}) => {
  const getSportNameById = (id: number) => {
    const sport = sportsData?.find((s) => s.sportId === id);
    return sport?.sportName || `Sport ${id}`;
  };

  const flattenedTournaments = useMemo<FlattenedTournament[]>(() => {
    if (!formState.tournaments) return [];

    const genders = ['male', 'female', 'mixed'];
    const flattened: FlattenedTournament[] = [];

    for (const sportIdStr of Object.keys(formState.tournaments)) {
      const sportId = Number(sportIdStr);
      const sport = (formState.tournaments as Record<number, any>)[sportId];
      if (!sport) continue;

      for (const gender of genders) {
        const genderObj = sport[gender];
        if (!genderObj) continue;

        for (const categoryIdStr of Object.keys(genderObj)) {
          const categoryId = categoryIdStr;
          const types = genderObj[categoryId];
          if (!Array.isArray(types)) continue;

          for (const typeId of types) {
            const typeName = TournamentTypeMap[typeId as TournamentType];
            flattened.push({
              sportId,
              gender,
              categoryName: categoryId,
              typeName,
            });
          }
        }
      }
    }

    return flattened;
  }, [formState.tournaments]);

  const totalTournaments = useMemo(
    () => flattenedTournaments.length,
    [flattenedTournaments],
  );

  if (isSportsLoading) {
    return (
      <LoadingContainer>
        <Spinner />
        <Text>Loading...</Text>
      </LoadingContainer>
    );
  }

  return (
    <Wrapper>
      <Header>
        <Title level={4} style={{ marginBottom: 4 }}>
          Review &amp; Confirm
        </Title>
        <Text type="secondary">
          Check all details before creating your event
        </Text>
      </Header>

      <EventCard>
        <Title level={5}>Event Details</Title>

        <Section>
          <Text type="secondary">Event Name</Text>
          <br />
          <Text>{formState.eventName}</Text>
        </Section>

        <Section>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Text type="secondary">Event Dates</Text>
              <br />
              <Text>
                {dayjs(formState.eventStartDate).format(
                  DateFormatStrings.DD_MMM_YYYY,
                )}{' '}
                -{' '}
                {dayjs(formState.eventEndDate).format(
                  DateFormatStrings.DD_MMM_YYYY,
                )}
              </Text>
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Registration Dates</Text>
              <br />
              <Text>
                {dayjs(formState.registrationStartDate).format(
                  DateFormatStrings.DD_MMM_YYYY,
                )}{' '}
                -{' '}
                {dayjs(formState.registrationEndDate).format(
                  DateFormatStrings.DD_MMM_YYYY,
                )}
              </Text>
            </Col>
          </Row>
        </Section>

        <Section>
          <Text type="secondary">Address</Text>
          <br />
          <Text>{formState.eventAddress}</Text>
        </Section>
      </EventCard>

      <EventCard>
        <Title level={5}>Tournaments ({totalTournaments})</Title>

        <List
          dataSource={flattenedTournaments}
          locale={{ emptyText: 'No tournaments selected' }}
          renderItem={(t, index) => (
            <StyledListItem
              key={`${t.sportId}-${t.gender}-${t.categoryName}-${t.typeName}-${index}`}
            >
              <ListItemInner>
                <Text strong>
                  {getSportNameById(t.sportId)} - {t.typeName}
                </Text>
                <br />
                <Text type="secondary">
                  {t.categoryName} {t.gender}
                </Text>
              </ListItemInner>
            </StyledListItem>
          )}
        />
      </EventCard>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;

const EventCard = styled(Card)`
  margin-bottom: 16px;
`;

const Section = styled.div`
  margin-bottom: 12px;
`;

const StyledListItem = styled(List.Item)`
  background: ${COLORS.grey.lightMedium};
  border-radius: 8px;
  margin-bottom: 8px;
`;

const ListItemInner = styled.div`
  width: 100%;
  padding-left: 12px;
`;

export default Step4Review;
